import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { saveDocumentToServer } from '../../apis/mediaAPI';
import Pencil from '../../assets/icons/pen-with-side-line--yellow.svg';
import ChatBox from '../../components/Chat/ChatBox';
import { FilterOrSelectBottomSheet } from '../../components/FilterOrSelectBottomSheet/FilterOrSelectBottomSheet';
import { RecipientsSummaryList } from '../../components/RecipientsSummaryList/RecipientsSummaryList';
import Loader from '../../components/Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useForm from '../../utils/customHooks/useForm';
import { translate } from '../../utils/translate';
import type { CreateMessageModel } from '../Chat/models';
import { sendAMessage } from '../CreateMessage/createMessageSlice/actionCreators';
import { selectGroupsByIds } from '../GroupsList/groupsSlice';
import { selectUserId } from '../Login/LoginSlice';
import { useTenantId } from '../../utils/customHooks/useTenantId';
import { getRecipientsText } from '../StartIamOkMessage/helpers';
import { SSummaryTab } from '../StartOnCallAlertMessage/StartOnCallAlertMessageSummary';
import { SIcon, SInput, SItem, SSimpleText } from '../StartOnCallAlertMessage/styles';
import { useCreateHoldingStatementCtx } from './CreateHoldingStatementContext';
import type { MessageSubjectForm } from './helpers';
import { messageSubjectSchema, useMembersWitPic } from './helpers';
import type { SelectedAllType } from '../../components/FilterOrSelectBottomSheet/types';
import { toRem } from '../../utils/toRem';
import { palette } from '../../theme/colors';
import { createMessageIsLoading } from '../CreateMessage/createMessageSlice';

const SContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`;

const SRecipientsSummaryListContainer = styled.div`
  margin: 0 ${toRem(20)};
  border-bottom: 1px solid ${palette.queenBlue};
`;

const SSSummaryTab = styled(SSummaryTab)`
  margin: 0 ${toRem(20)};
`;

const SsItem = styled(SItem)`
  margin: 0 ${toRem(20)};
`;

export const CreateHoldingStatmentSummary = () => {
  const [tabBar, setTabBar] = React.useState(true);
  const loading = useAppSelector(createMessageIsLoading);
  const [recipientsFilter, setRecipientsFilter] = React.useState(false);
  const subjectRef = React.useRef<HTMLInputElement>(null);
  const tenantId = useTenantId();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { groupIds, setGroupIds, userIds, setUserIds, subject, setSubject } =
    useCreateHoldingStatementCtx();

  const groups = useAppSelector(selectGroupsByIds([...groupIds]));
  const userId = useAppSelector(selectUserId);

  const goToSelectGroups = () => {
    navigate('/createHoldingStatement');
  };

  React.useEffect(() => {
    if (groupIds.size === 0) {
      goToSelectGroups();
      return;
    }
  }, [groupIds]);

  let mounted = true;
  React.useEffect(() => {
    return () => {
      mounted = false;
    };
  }, []);

  const { members: membersForFilter, isGettingMembers } = useMembersWitPic({
    ids: [...groupIds],
    setUserIds: setUserIds,
    tenantId,
  });
  const { inputs, handleChange, errors, handleBlur, touched, setErrors } =
    useForm<MessageSubjectForm>({
      initial: {
        subject: subject ?? '',
      },
      validateSchema: {
        subject: messageSubjectSchema,
      },
    });

  const focusSubject = () => {
    if (!inputs.subject) {
      if (mounted) setErrors({ ...touched, subject: 'error' });
    }
  };
  const sendMessage = async (
    text?: string,
    imageFileNames?: string[],
    documentFileNames?: string[],
    audioFileNames = [],
    locationId?: number
  ) => {
    const messageModel: CreateMessageModel = {
      subject: inputs.subject,
      senderId: userId,
      groupIds: [...groupIds],
      recipientIds: [...userIds],
      documentFileNames: documentFileNames,
      audioFileNames: audioFileNames,
      photoFileNames: imageFileNames,
      text: text,
      locationId: locationId,
      type: 9,
    };

    await dispatch(sendAMessage(messageModel, navigate, undefined, true));
  };

  const onPhotosSend = async (imagesList: string[], messageText: string) => {
    if (imagesList.length > 0) {
      const text = messageText && messageText.length > 0 ? messageText : undefined;
      await sendMessage(text, imagesList);
    }
  };

  const onDocumentsSend = async (documentsList: File[]) => {
    const documentsFilesNamesArray = [];
    let document: File;
    for await (document of documentsList) {
      const formData = new FormData();
      formData.append('document', document);
      const result = await saveDocumentToServer(formData);
      if (result) {
        documentsFilesNamesArray.push(result);
      }
    }
    if (documentsFilesNamesArray.length > 0) {
      await sendMessage(undefined, [], documentsFilesNamesArray);
    }
  };

  const onLocationSend = (locationId: number, text?: string | undefined) => {
    sendMessage(text, [], [], [], locationId);
  };

  const deleteContact = (id: number) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setGroupIds(prev => {
      const filterdSet = new Set(prev);
      filterdSet.delete(id);
      if (filterdSet.size === 0) {
        goToSelectGroups();
      }
      return filterdSet;
    });
  };

  const onFilter = (selected: Set<number> | SelectedAllType) => {
    switch (selected) {
      case 'SELECTED_ALL': {
        const selectedIds = new Set(membersForFilter.map(member => member.userID));
        setUserIds(selectedIds);
        setRecipientsFilter(false);
        break;
      }
      case 'UNSELECTED_ALL':
        break;
      default:
        setUserIds(selected);
        setRecipientsFilter(false);
        break;
    }
  };

  const highlightText = () => {
    if (subjectRef.current) subjectRef.current.focus();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e as React.ChangeEvent<HTMLInputElement>);
    setSubject(e.target.value);
  };

  if (loading) return <Loader />;
  return (
    <SContainer role="summaryPage">
      <div role="summaryContainer">
        {!isGettingMembers && (
          <FilterOrSelectBottomSheet
            isOpen={recipientsFilter}
            setIsOpen={setRecipientsFilter}
            onFilter={onFilter}
            data={membersForFilter}
            initialSelected={userIds}
            withPhoto
            atLeastOneReq
            selectShapeType="box"
            titleTx="messages_selectRecepients"
            selectAllTx={'imOk_allRecepients'}
            atLeastOneReqTx="message_create_selectAtLeastOneGroup"
            hideCurrentUserId={userId}
          />
        )}
        <SRecipientsSummaryListContainer>
          <RecipientsSummaryList
            data={groups}
            handleAddClick={goToSelectGroups}
            handleRemoveClick={deleteContact}
            recipientGroupsRoute={'/createHoldingStatement/recipient-groups'}
          />
        </SRecipientsSummaryListContainer>
        <SSSummaryTab
          title={translate('messages_to')}
          summaryText={getRecipientsText(membersForFilter, userIds || new Set())}
          onTabClick={() => setRecipientsFilter(true)}
        />
        <SsItem className="input" role="subjectInputContainer">
          <SSimpleText role="placeholder" className="left" fontSize="12px">
            {translate(`messages_subject_add`)}
          </SSimpleText>
          <SInput
            name="subject"
            value={inputs.subject}
            onChange={onChange}
            onBlur={handleBlur}
            $error={!!errors.subject}
            type="text"
            placeholder={translate(`messages_subject_add_placeholder`)}
            ref={subjectRef}
            role="subjectInput"
          />

          <SIcon role="editIcon" src={Pencil} alt="" onClick={highlightText} />
        </SsItem>
      </div>
      <ChatBox
        tabBar={tabBar}
        setTabBar={setTabBar}
        onTextSend={sendMessage}
        onPhotosSend={onPhotosSend}
        onDocumentsSend={onDocumentsSend}
        onLocationSend={onLocationSend}
        dependencyRequired
        isDependencyAdded={!!inputs.subject}
        onDependencyReFocus={focusSubject}
      />
    </SContainer>
  );
};
